<script setup lang="ts">
import { ref, toRef } from 'vue';

import { useI18n } from 'vue-i18n';
import ReorderableTable from '@/components/ReorderableTable.vue';
import type { Locales, MapCategoryDto } from '@/services/types/api.types';
import MapCategoryEditDialog from '@/components/dialogs/MapCategoryEditDialog.vue';
import { mapMarkers } from '@/helpers/map-style';
import { useQuasar } from 'quasar';
import { useFormsStore } from '@/stores';
import { getTranslation, provideEditLocale } from '@/composables/data-display-utils';

// PROPS
const props = defineProps<{
  categories: MapCategoryDto[];
  formId: number;
  stepId: number;
  languageEnabled: Locales[];
}>();

// CONST
const formsStore = useFormsStore();
const $q = useQuasar();
const { t } = useI18n();

provideEditLocale(toRef(() => props.languageEnabled));

// EMIT
const emit = defineEmits<{
  (e: 'addCategory', category: object): void;
}>();

// FUNCTIONS
const handleRowClick = (row: MapCategoryDto) => {
  $q.dialog({
    component: MapCategoryEditDialog,
    componentProps: {
      mode: 'edit',
      category: row,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleAddRow = () => {
  const mapCategory = formsStore.createMapCategory();
  $q.dialog({
    component: MapCategoryEditDialog,
    componentProps: {
      mode: 'add',
      category: mapCategory,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleDeleteRow = (row: MapCategoryDto) => {
  $q.dialog({
    title: t('form.dialogs.map-category-row.delete-title'),
    message: t('form.dialogs.map-category-row.delete-message'),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(() => {
    formsStore.deleteMapCategory(row, props.formId, props.stepId);
  });
};
const columns = ref([
  {
    name: 'text',
    label: t('form.map-category.fields.name'),
    align: 'left',
    field: (row: any) => getTranslation(row, 'name', props.languageEnabled),
  },
  { name: 'image', label: t('form.map-category.fields.marker-color'), align: 'center', field: (row: any) => row.color },
]);
</script>
<template>
  <div class="category-container">
    <reorderable-table
      :rows="props.categories"
      :columns="columns"
      :title="$t('form.map-category.list-title')"
      itemKey="id"
      :disableOrderable="true"
      @rowClick="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
    >
      <template v-slot:body-cell-image="props">
        <q-td :props="props">
          <img :src="mapMarkers.find((marker) => marker.value === props.row.color)?.img" />
        </q-td>
      </template>
    </reorderable-table>
  </div>
</template>
<style lang="scss">
.category-container {
  display: flex;
  flex-direction: column;

  &__category {
    display: flex;
    flex-direction: column;
    margin-top: 10px;
  }

  &__new {
    display: flex;
    justify-content: center;
    margin-left: 14px;
    margin-top: 10px;
    align-items: center;
    gap: 5px;

    .q-btn {
      background-color: $color-neutral-grey-300;
      width: 100%;
    }
  }
}
</style>
